import React from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { AddCircleOutline, DeleteOutline } from '@mui/icons-material';
import { CabinetTopology } from '../models/cabinet';

export interface DeviceRow {
  serial: string,
  isPrimary: boolean,
  isMaster: boolean,
}

interface DeviceRosterFieldsProps {
  topology: CabinetTopology,
  onTopologyChange(topology: CabinetTopology): void,
  devices: DeviceRow[],
  onDevicesChange(devices: DeviceRow[]): void,
  // Optional "already claimed" pre-check, keyed by trimmed serial.
  serialClaimWarnings?: Record<string, string>,
  onSerialBlur?(serial: string): void,
}

// Shared topology roster editor used by both the cabinet creator (registration)
// and the cabinet editor. Master requires primary (courier/pickup); a device with
// primary off is "crew". Exactly one master is allowed across the roster.
function DeviceRosterFields({
  topology,
  onTopologyChange,
  devices,
  onDevicesChange,
  serialClaimWarnings = {},
  onSerialBlur,
}: DeviceRosterFieldsProps) {
  const masterTaken = devices.some((d) => d.isMaster);

  // Count each trimmed serial so we can flag ones entered on more than one row.
  const serialCounts = devices.reduce<Record<string, number>>((acc, d) => {
    const trimmed = d.serial.trim();
    if (trimmed) {
      acc[trimmed] = (acc[trimmed] || 0) + 1;
    }
    return acc;
  }, {});

  const updateDeviceSerial = (index: number, serial: string) => {
    onDevicesChange(devices.map((d, i) => (i === index ? { ...d, serial } : d)));
  };

  const togglePrimary = (index: number, checked: boolean) => {
    // Turning primary off also clears master (master requires primary).
    onDevicesChange(devices.map((d, i) => (i === index ? { ...d, isPrimary: checked, isMaster: checked ? d.isMaster : false } : d)));
  };

  const toggleMaster = (index: number, checked: boolean) => {
    onDevicesChange(devices.map((d, i) => (i === index ? { ...d, isMaster: checked } : d)));
  };

  const addDevice = () => {
    // New rows default to crew (primary off) since the master is typically the first device.
    onDevicesChange([...devices, { serial: '', isPrimary: false, isMaster: false }]);
  };

  const removeDevice = (index: number) => {
    onDevicesChange(devices.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ width: '520px' }}>
      <Typography sx={{ marginBottom: '8px' }}>Topology</Typography>
      <Select
        value={topology}
        onChange={(e: SelectChangeEvent) => {
          onTopologyChange(e.target.value as CabinetTopology);
        }}
        sx={{ width: '100%', marginBottom: '24px' }}>
        <MenuItem value='single_tablet_external'>Single Tablet + External Screen</MenuItem>
        <MenuItem value='multi_tablet'>Multiple Tablets</MenuItem>
      </Select>

      {topology === 'multi_tablet' && (
        <Box>
          <Typography sx={{ fontWeight: 500, marginBottom: '4px' }}>Devices</Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', marginBottom: '16px' }}>
            Add each tablet&apos;s serial. You need at least one Master device (Courier/Pickup + Master) and at least one Crew device (Courier/Pickup off) to continue. Exactly one master is allowed.
          </Typography>

          {devices.map((device, index) => {
            const trimmedSerial = device.serial.trim();
            const isDuplicate = !!trimmedSerial && serialCounts[trimmedSerial] > 1;
            const serialError = isDuplicate ? 'Duplicate serial — already entered on another row' : (serialClaimWarnings[trimmedSerial] || '');
            return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: '16px',
              }}>
              <TextField
                sx={{ flexGrow: 1, marginRight: '16px' }}
                inputProps={{ maxLength: 24 }}
                required
                label={`Device ${index + 1} Serial`}
                value={device.serial}
                error={!!serialError}
                helperText={serialError}
                onBlur={(e) => {
                  if (onSerialBlur) {
                    onSerialBlur(e.target.value);
                  }
                }}
                onChange={(e) => {
                  updateDeviceSerial(index, e.target.value);
                }} />

              <Box sx={{ textAlign: 'center', marginRight: '8px', minWidth: '96px' }}>
                <Typography variant='body2'>Courier/Pickup</Typography>
                <Switch
                  checked={device.isPrimary}
                  onChange={(e) => {
                    togglePrimary(index, e.target.checked);
                  }} />
              </Box>

              {device.isPrimary && (!masterTaken || device.isMaster) && (
                <Box sx={{ textAlign: 'center', marginRight: '8px', minWidth: '64px' }}>
                  <Typography variant='body2'>Master</Typography>
                  <Switch
                    checked={device.isMaster}
                    onChange={(e) => {
                      toggleMaster(index, e.target.checked);
                    }} />
                </Box>
              )}

              {!device.isPrimary && (
                <Box sx={{ textAlign: 'center', marginRight: '8px', minWidth: '64px' }}>
                  <Typography variant='body2' sx={{ color: 'text.secondary' }}>Crew</Typography>
                </Box>
              )}

              <IconButton
                aria-label='remove device'
                disabled={devices.length <= 1}
                onClick={() => {
                  const label = trimmedSerial || `Device ${index + 1}`;
                  if (window.confirm(`Remove ${label} from the roster?`)) {
                    removeDevice(index);
                  }
                }}>
                <DeleteOutline />
              </IconButton>
            </Box>
            );
          })}

          <Button
            startIcon={<AddCircleOutline />}
            onClick={addDevice}>
            Add device
          </Button>
        </Box>
      )}
    </Box>
  );
}

// Validates a multi-tablet roster: all serials filled, exactly one master, and
// at least one crew device. Single-tablet topology is always valid.
export function isDeviceRosterValid(topology: CabinetTopology, devices: DeviceRow[]): boolean {
  if (topology !== 'multi_tablet') {
    return true;
  }
  const serials = devices.map((d) => d.serial.trim());
  const allSerialsFilled = serials.every((s) => s.length > 0);
  const noDuplicates = new Set(serials).size === serials.length;
  const masterCount = devices.filter((d) => d.isPrimary && d.isMaster).length;
  const hasCrew = devices.some((d) => !d.isPrimary);
  return allSerialsFilled && noDuplicates && masterCount === 1 && hasCrew;
}

export default DeviceRosterFields;
